import { useEffect, useState } from 'react';
import { patch, put } from '../../data/mutate';
import { selectSettings } from '../../data/selectors';
import { getState, useStore } from '../../data/store';
import { api, apiBase } from '../../lib/api';
import { copyText, isNative } from '../../lib/platform';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import { ListGroup, ListRow } from '../../ui/List';
import { Page } from '../../ui/Page';
import { toast } from '../../ui/Toast';
import s from './SettingsScreen.module.css';

/** This copy's own subdomain, like "denis" for denis.tripsapp.app; null for the main one. */
const SUB = (() => {
  if (isNative || typeof location === 'undefined') return null;
  const m = /^([a-z0-9-]+)\.tripsapp\.app$/.exec(location.hostname);
  return m?.[1] ?? null;
})();

/** Where Email Routing delivers forwarded bookings: each copy of the app has its own. */
export const IMPORT_ADDRESS = SUB ? `${SUB}@tripsapp.app` : 'trips@tripsapp.app';

/** The Claude connector lives at /mcp on the app's own origin. */
function connectorUrl(): string {
  const base = apiBase();
  return `${isNative || !/^https:\/\//.test(base) ? 'https://tripsapp.app' : base}/mcp`;
}

async function copy(text: string) {
  const ok = await copyText(text);
  toast(ok ? 'Copied' : "Couldn't copy. Select the text instead.");
}

function setAllowlist(list: string[]) {
  if (getState().tables.settings.get('settings'))
    void patch('settings', 'settings', { senderAllowlist: list });
  else void put('settings', { ...selectSettings(getState()), senderAllowlist: list });
}

function looksLikeSender(v: string) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) || /^@?[^@\s]+\.[^@\s]+$/.test(v);
}

/** Forwarding address, allowed senders, and connecting Claude (claude.ai and the phone app). */
export function ClaudeImportPage() {
  const allowlist = useStore((st) => selectSettings(st).senderAllowlist);
  const [draft, setDraft] = useState('');
  const [auto, setAuto] = useState<boolean | null>(null);
  const url = connectorUrl();

  useEffect(() => {
    api<{ autoImport: boolean }>('/api/claude/status')
      .then((r) => setAuto(r.autoImport))
      .catch(() => setAuto(false));
  }, []);

  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!looksLikeSender(v)) {
      toast('Enter an email address, or a domain like @example.com.', { tone: 'error' });
      return;
    }
    if (!allowlist.includes(v)) setAllowlist([...allowlist, v]);
    setDraft('');
  };

  return (
    <Page title="Claude & email import" backTo="/settings">
      <ListGroup
        title="Forward bookings"
        footer="Forward confirmation emails here from an allowed address. Each one lands in the Inbox, then becomes plans in the right trip."
      >
        <ListRow
          icon="envelope-simple"
          title={<span className="num">{IMPORT_ADDRESS}</span>}
          trailing={
            <Button size="sm" icon="copy-simple" onClick={() => void copy(IMPORT_ADDRESS)}>
              Copy
            </Button>
          }
        />
        <ListRow icon="tray" title="Inbox" to="/inbox" />
      </ListGroup>

      <ListGroup
        title="Allowed senders"
        footer="Mail from anyone else is turned away. Add the addresses you forward from, or a whole domain like @example.com."
      >
        {allowlist.length === 0 && (
          <ListRow title="No one yet" detail="Add your own address first" />
        )}
        {allowlist.map((a) => (
          <ListRow
            key={a}
            title={a}
            trailing={
              <Button
                size="sm"
                variant="danger"
                onClick={() => setAllowlist(allowlist.filter((x) => x !== a))}
              >
                Remove
              </Button>
            }
          />
        ))}
      </ListGroup>
      <form
        className={s.inlineForm}
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <TextField
          label="Add a sender"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={draft}
          onChange={setDraft}
        />
        <Button type="submit" icon="plus" disabled={!draft.trim()}>
          Add
        </Button>
      </form>

      <ListGroup
        title="Reading emails"
        footer={
          auto
            ? 'Forwarded emails are read automatically and added to your trips.'
            : 'Forwarded emails wait in the Inbox. Ask Claude to "import my trips inbox" and it reads them for you.'
        }
      >
        <ListRow
          icon={auto ? 'check-circle' : 'info'}
          title="Automatic reading"
          value={auto == null ? 'Checking' : auto ? 'On' : 'Off'}
        />
      </ListGroup>

      <ListGroup
        title="Connect Claude"
        footer="Then ask Claude about your trips, or send it a booking, a screenshot or a PDF and ask it to add it. Works in the Claude phone app too."
      >
        <ListRow
          icon="link"
          title={<span className="num">{url}</span>}
          trailing={
            <Button size="sm" icon="copy-simple" onClick={() => void copy(url)}>
              Copy
            </Button>
          }
        />
      </ListGroup>
      <ol className={`${s.steps} t-footnote`}>
        <li>On claude.ai, open Settings, then Connectors.</li>
        <li>Choose Add custom connector and paste the URL above.</li>
        <li>Choose Connect, sign in here if asked, and choose Allow.</li>
        <li>It's now on in the Claude phone app as well.</li>
      </ol>
    </Page>
  );
}
